import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Input,
    Text,
    VStack,
    HStack,
    Spinner,
    Dialog,
    Alert,
    Flex,
} from '@chakra-ui/react';
import { FiUserPlus } from 'react-icons/fi';
import axios from 'axios';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

const SuggestAgentModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        suggested_name: '',
        suggested_email: '',
    });
    const [activeApprover, setActiveApprover] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingApprover, setLoadingApprover] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchActiveApprover();
            setFormData({ suggested_name: '', suggested_email: '' });
            setError(null);
        }
    }, [isOpen]);

    const fetchActiveApprover = async () => {
        try {
            setLoadingApprover(true);
            const response = await axios.get(`${API_URL}/api/agent-suggestions/active-approver`, {
                withCredentials: true,
            });
            setActiveApprover(response.data.approver);
        } catch (error) {
            console.error('Error fetching active approver:', error);
        } finally {
            setLoadingApprover(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'suggested_name' ? value.toUpperCase() : value,
        }));
    };

    const handleSubmit = async () => {
        if (!formData.suggested_name.trim() || !formData.suggested_email.trim()) {
            setError('Please fill in all required fields');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.suggested_email)) {
            setError('Invalid email format');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await axios.post(
                `${API_URL}/api/agent-suggestions`,
                formData,
                { withCredentials: true }
            );

            alert('Suggestion submitted successfully! Your suggestion has been sent for approval.');
            onClose();
        } catch (error) {
            console.error('Error submitting suggestion:', error);
            setError(error.response?.data?.error || 'Failed to submit suggestion');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog.Root open={true} onOpenChange={(e) => !e.open && onClose()}>
            <Dialog.Backdrop bg="blackAlpha.600" />
            <Dialog.Positioner>
                <Dialog.Content maxW="md" borderRadius="xl" mx={4} bg="white">
                    <Dialog.Header
                        bg="purple.50"
                        borderTopRadius="xl"
                        borderBottom="1px"
                        borderColor="gray.200"
                        py={4}
                        px={6}
                    >
                        <Flex justify="space-between" align="center" w="100%">
                            <HStack>
                                <FiUserPlus />
                                <Text fontSize="lg" fontWeight="bold" color="purple.700">
                                    Suggest New Agent
                                </Text>
                            </HStack>
                        </Flex>
                    </Dialog.Header>
                    <Dialog.CloseTrigger />

                    <Dialog.Body py={6} px={6}>
                        <VStack spacing={4} align="stretch">
                            {error && (
                                <Alert.Root status="error" borderRadius="md">
                                    <Alert.Indicator />
                                    <Alert.Content>
                                        <Alert.Description>{error}</Alert.Description>
                                    </Alert.Content>
                                </Alert.Root>
                            )}

                            {loadingApprover ? (
                                <HStack justify="center">
                                    <Spinner size="sm" />
                                    <Text fontSize="sm" color="gray.500">Loading...</Text>
                                </HStack>
                            ) : activeApprover ? (
                                <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                                    <Text fontSize="sm" color="blue.700">
                                        <strong>Approver:</strong> {activeApprover.approver_name} ({activeApprover.approver_email})
                                    </Text>
                                </Box>
                            ) : (
                                <Box p={3} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
                                    <Text fontSize="sm" color="yellow.700">
                                        No approver configured. Your suggestion will be reviewed by Admin/Editor.
                                    </Text>
                                </Box>
                            )}

                            <Box>
                                <Text fontSize="sm" fontWeight="medium" mb={1}>Agent Name *</Text>
                                <Input
                                    name="suggested_name"
                                    value={formData.suggested_name}
                                    onChange={handleChange}
                                    placeholder="Enter agent name"
                                />
                            </Box>

                            <Box>
                                <Text fontSize="sm" fontWeight="medium" mb={1}>Agent Email *</Text>
                                <Input
                                    name="suggested_email"
                                    type="email"
                                    value={formData.suggested_email}
                                    onChange={handleChange}
                                    placeholder="Enter agent email"
                                />
                            </Box>
                        </VStack>
                    </Dialog.Body>

                    <Dialog.Footer
                        bg="gray.50"
                        borderBottomRadius="xl"
                        borderTop="1px"
                        borderColor="gray.200"
                        px={6}
                        py={4}
                    >
                        <HStack spacing={3} w="100%" justify="flex-end">
                            <Button variant="outline" onClick={onClose} disabled={loading}>
                                Cancel
                            </Button>
                            <Button
                                colorPalette="purple"
                                onClick={handleSubmit}
                                loading={loading}
                                disabled={loading}
                                loadingText="Submitting..."
                            >
                                Submit Suggestion
                            </Button>
                        </HStack>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    );
};

export default SuggestAgentModal;
